import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * PortalOverlay — uniwersalny wrapper dla wszystkich overlay/modali.
 *
 * Dlaczego portal?
 *   Wyrenderowanie modala bezpośrednio do `document.body` chroni go przed problemami
 *   z `position: fixed`, gdy jakiś rodzic ma `transform`, `filter`, `backdrop-filter`,
 *   `will-change` albo `perspective` (te właściwości tworzą "containing block" i fixed
 *   pozycjonuje się względem nich, nie viewportu).
 *
 * Bonusy:
 *   - Lock scroll body gdy `lockScroll=true` (domyślnie ON)
 *   - Obsługa klawisza Escape → wywołuje onClose (gdy `closeOnEscape=true`)
 *
 * Używaj WSZĘDZIE tam, gdzie ma być pełnoekranowy overlay/modal:
 *   <PortalOverlay onClose={...}>
 *     <div style={{ position:'fixed', inset:0, ... }}> ... </div>
 *   </PortalOverlay>
 */
const PortalOverlay = ({
  children,
  isOpen = true,
  onClose,
  lockScroll = true,
  closeOnEscape = true,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (closeOnEscape && e.key === 'Escape' && typeof onClose === 'function') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      if (lockScroll) document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, lockScroll, closeOnEscape, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

export default PortalOverlay;
